import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import AuthService from './services/AuthService';
import fileService from './services/FileService';
import baseService from './services/BaseService';
import MultipleFileUpload from './components/MultipleFileUpload';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, Select, MenuItem, TableHead, TableRow, Checkbox, Button, Snackbar, Alert, IconButton, TextField, InputAdornment, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

function Files() {
    const [files, setFiles] = useState([]);
    const [bases, setBases] = useState([]);
    const [selectedBase, setSelectedBase] = useState('');

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [searchTerm, setSearchTerm] = useState('');
    const { t } = useTranslation();
    const loginAs = AuthService.getLoginAs();
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
    const [refreshDialogFile, setRefreshDialogFile] = useState(null);
    const [refreshDialogValue, setRefreshDialogValue] = useState('Never');
    const [updatingRefresh, setUpdatingRefresh] = useState(false);

    const fetchFiles = async (s3_path) => {
        try {
            setLoadingFiles(true);
            const loginAsUri = loginAs ? `?loginAs=${loginAs}` : '';
            const s3_pathOverride = s3_path ? s3_path : selectedBase;
            const data = await fileService.getFiles(s3_pathOverride, loginAsUri);
            const safeData = Array.isArray(data) ? data : [];
            setFiles(safeData);

            // Calculate total file size
        } catch (error) {
            console.error('Error fetching files:', error);
            setFiles([]);
            setSnackbarMessage(t('files.fetchErrorMessage', 'Failed to load files'));
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setLoadingFiles(false);
        }
    };

    const handleBaseChange = (event) => {
        console.log(`handleBaseChange ${event.target.value}`)
        setSelectedBase(event.target.value);
        fetchFiles(event.target.value);
        // Add any additional logic needed when the base changes
    };

    const fetchBases = async () => {
        try {
            const bases = await baseService.getBases(loginAs);
            // console.log(`Fetched Bases: ${JSON.stringify(bases)}`);
            const s3_path = bases[0]['s3_path'];
            // console.log(`Selected Base0: ${baseId}`);
            setSelectedBase(s3_path);
            setBases(bases);

            // console.log(`calling fetch file: ${selectedBase}`);
            fetchFiles(s3_path);
            // Transient message after base selection and initial fetch
            const baseName = bases[0].base_name
            setSnackbarMessage(`${t('welcomeBar.selectedBase')}: ${baseName}`);
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Error fetching bases:', error);
        }
    };
    useEffect(() => {
        fetchBases();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelectFile = (fileId) => {
        setSelectedFiles((prevSelected) =>
            prevSelected.includes(fileId) ? prevSelected.filter((id) => id !== fileId) : [...prevSelected, fileId]
        );
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Select all visible rows by their document IDs
            setSelectedFiles(filteredFiles.map((file) => file.documentID));
        } else {
            setSelectedFiles([]);
        }
    };

    const handleDeleteSelected = () => {
        setOpenConfirmDialog(true); // Open the confirmation dialog
    };

    const confirmDelete = async () => {
        try {
            console.log(`selectedFiles: ${selectedFiles}`);
            const deletePromises = selectedFiles.map((documentID) =>
                fileService.deleteFile(selectedBase, documentID)
            );
            await Promise.all(deletePromises);

            // Filter out deleted files from the state
            setFiles((prevFiles) => prevFiles.filter((file) => !selectedFiles.includes(file.documentID)));
            setSnackbarMessage(`${selectedFiles.length} ${t('files.deletedMessage')}`);
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
            setSelectedFiles([]);
        } catch (error) {
            setSnackbarMessage(t('files.deleteErrorMessage'));
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
            console.error('Error deleting files:', error);
        }
        setOpenConfirmDialog(false); // Close the confirmation dialog
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleUploadComplete = () => {
        setSnackbarMessage(t('files.uploadSuccess'));
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
        setTimeout(() => {
            fetchFiles();
        }, 5000);
        setTimeout(() => {
            fetchFiles();
        }, 10000);
    };

    const handleRefresh = () => {
        fetchFiles();
    };

    const filteredFiles = files.filter((file) =>
        file.documentName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Auto-refresh every 10s while there are files pending OCR (prefix 'pocr-')
    const refreshIntervalRef = useRef(null);
    useEffect(() => {
        const hasPocr = files?.some((f) => f?.documentName?.startsWith('pocr-'));

        // Always clear any existing interval when files or base changes
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }

        if (hasPocr) {
            refreshIntervalRef.current = setInterval(() => {
                fetchFiles();
            }, 10000);
        }

        // Cleanup on unmount or dependency change
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files, selectedBase]);

    // Display-only transformations:
    // - If name starts with 'pocr-': remove prefix for display name and set status to 'Processing'
    // - If name starts with 'ocr-' and ends with '.pdf.txt': show as '<original>-ocr.pdf' and map type to 'pdf'
    const getDisplayForFile = (file) => {
        const name = file?.documentName || '';
        let displayName = name;
        let displayType = file?.type || '';
        let displayStatus = file?.status || '';

        if (name.startsWith('failed.') && name.endsWith('.txt')) {
            const originalName = name.slice('failed.'.length, -'.txt'.length) || name;
            displayName = originalName;
            displayStatus = 'Failed';
            const originalExtension = originalName.split('.').pop()?.toLowerCase();
            if (originalExtension) {
                displayType = originalExtension;
            }
        }

        // Pending OCR: pocr-<name> -> <name>, status: Processing
        if (name.startsWith('pocr-')) {
            displayName = name.slice(5); // remove 'pocr-'
            displayStatus = 'Processing';
        }

        if (name.startsWith('ocr-')) {
            if (name.endsWith('.pdf.txt')) {
                const core = name.slice(4, name.length - '.pdf.txt'.length); // remove 'ocr-' and trailing '.pdf.txt'
                displayName = `${core}-ocr.pdf`;
                displayType = 'pdf';
            } else if (name.endsWith('.txt')) {
                // Generic: ocr-<base>.<ext>.txt -> <base>-ocr.<ext>
                const withoutPrefixSuffix = name.slice(4, -4); // strip 'ocr-' and trailing '.txt'
                const match = withoutPrefixSuffix.match(/^(.*)\.([a-zA-Z0-9]+)$/);
                if (match) {
                    const base = match[1];
                    const ext = match[2];
                    displayName = `${base}-ocr.${ext}`;
                    if (ext.toLowerCase() === 'pdf') {
                        displayType = 'pdf';
                    }
                } else {
                    displayName = `${withoutPrefixSuffix}-ocr`;
                }
            }
        }

        if ((displayStatus || '').toLowerCase() === 'failed') {
            const failedPrefix = 'failed.';
            const failedSuffix = '.txt';
            if (displayName.startsWith(failedPrefix) && displayName.endsWith(failedSuffix)) {
                displayName = displayName.slice(failedPrefix.length, -failedSuffix.length) || displayName;
            }
        }

        const normalizedDisplayName = String(displayName || '').trim();
        if (/^https?:\/\//i.test(normalizedDisplayName)) {
            displayType = 'url';
        } else {
            const filenameWithoutQuery = normalizedDisplayName.split('?')[0].split('#')[0];
            const extension = filenameWithoutQuery.includes('.')
                ? filenameWithoutQuery.split('.').pop()?.trim().toLowerCase()
                : '';
            if (extension) {
                displayType = extension;
            }
        }

        // Display mapping: if backend type is 'table', show 'csv'
        if (displayType && displayType.toLowerCase() === 'table') {
            displayType = 'csv';
        }

        return { displayName, displayType, displayStatus };
    };

    return (
        <Box
            sx={{
                mx: -3,
                mt: -3,
                mb: -3,
                width: 'calc(100% + 48px)',
                minHeight: '100vh',
                px: { xs: 2, md: 5 },
                pt: 5.5,
                pb: 4,
                backgroundColor: '#ffffff',
            }}
        >
            {/* Header Section */}
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2.4, gap: 2, flexWrap: 'wrap' }}>
                <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '1.45rem', fontWeight: 700, color: '#000000', lineHeight: 1.2 }}>
                        {selectedBase ? `${t('files.knowledgeBases')}:` : t('files.noKnowledgeBases')}
                    </Typography>

                    {selectedBase && (
                        <Select
                            value={selectedBase}
                            onChange={handleBaseChange}
                            displayEmpty
                            variant="outlined"
                            size="small"
                            sx={{
                                minWidth: 90,
                                height: 48,
                                borderRadius: '4px',
                                backgroundColor: '#ffffff',
                                color: '#222222',
                                fontSize: '1rem',
                                '& .MuiSelect-select': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    py: 1.2,
                                    px: 1.6,
                                },
                                '& fieldset': { borderColor: '#cfcfcf' },
                                '&:hover fieldset': { borderColor: '#bdbdbd' },
                                '&.Mui-focused fieldset': { borderColor: '#9e9e9e' },
                            }}
                        >
                            <MenuItem value="" disabled>
                                {t('files.selectBase')}
                            </MenuItem>
                            {bases.map((base) => (
                                <MenuItem key={base.s3_path} value={base.s3_path}>
                                    {base.base_name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </Box>

                {selectedBase && (
                    <Box display="flex" alignItems="center" gap={2}>
                        <IconButton onClick={handleRefresh} sx={{ color: '#1976d2' }}>
                            <RefreshIcon />
                        </IconButton>
                        <MultipleFileUpload onUploadComplete={handleUploadComplete} baseId={selectedBase} />
                    </Box>
                )}
            </Box>

            {/* Search Bar */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'left' }}>
                <TextField
                    variant="outlined"
                    placeholder={t('files.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    sx={{
                        width: { xs: '100%', sm: 585 },
                        '& .MuiOutlinedInput-root': {
                            height: 50,
                            borderRadius: '4px',
                            backgroundColor: '#ffffff',
                            fontSize: '1rem',
                            '& fieldset': { borderColor: '#cfcfcf' },
                            '&:hover fieldset': { borderColor: '#bdbdbd' },
                            '&.Mui-focused fieldset': { borderColor: '#9e9e9e' },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#7a7a7a', fontSize: 26 }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Files Table */}
            <TableContainer
                sx={{
                    mt: 2,
                    borderRadius: '8px 8px 0 0',
                    overflow: 'hidden',
                    boxShadow: 'none',
                    backgroundColor: '#ffffff',
                }}
            >
                {!loadingFiles && (
                <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                padding="checkbox"
                                sx={{
                                    width: 64,
                                    backgroundColor: '#ffffff',
                                    borderBottom: '1px solid #dddddd',
                                    py: 0.85,
                                }}
                            >
                                <Checkbox
                                    checked={selectedFiles.length === filteredFiles.length && filteredFiles.length > 0}
                                    indeterminate={selectedFiles.length > 0 && selectedFiles.length < filteredFiles.length}
                                    onChange={handleSelectAll}
                                    sx={{ color: '#777777', '&.Mui-checked': { color: '#1976d2' }, '&.MuiCheckbox-indeterminate': { color: '#1976d2' } }}
                                />
                            </TableCell>
                            {[
                                { label: `${t('files.documentName')} (${files.length} ${t('files.documentCount')})`, width: '49%' },
                                { label: t('files.type'), width: '12%' },
                                { label: t('files.lastModified'), width: '18%' },
                                { label: t('files.refreshRate'), width: '10%' },
                                { label: t('files.status'), width: '11%' },
                            ].map((column, index) => (
                                <TableCell key={column.label} sx={{
                                    width: column.width,
                                    fontWeight: 700,
                                    fontSize: '0.92rem',
                                    color: '#2a2a2a',
                                    backgroundColor: '#eeeeee',
                                    borderBottom: '1px solid #dddddd',
                                    borderTopLeftRadius: index === 0 ? '8px' : 0,
                                    borderTopRightRadius: index === 4 ? '8px' : 0,
                                    py: 1.35,
                                }}>
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredFiles.map((file) => {
                            const { displayName, displayType, displayStatus } = getDisplayForFile(file);
                            const statusNorm = (displayStatus || '').toUpperCase();
                            const localizedStatus = t(`files.statusValues.${statusNorm}`, displayStatus);
                            return (
                            <TableRow
                                key={file.documentID}
                                sx={{
                                    '&:hover td': { backgroundColor: '#fafafa', transition: 'background-color 0.15s ease' },
                                }}
                            >
                                <TableCell padding="checkbox" sx={{ borderBottom: '1px solid #dddddd', py: 0.95 }}>
                                    <Checkbox
                                        checked={selectedFiles.includes(file.documentID)}
                                        onChange={() => handleSelectFile(file.documentID)}
                                        sx={{ color: '#777777', '&.Mui-checked': { color: '#1976d2' } }}
                                    />
                                </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #dddddd', py: 0.95, color: '#2a2a2a', fontSize: '0.92rem' }}>{displayName}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #dddddd', py: 0.95, color: '#2a2a2a', fontSize: '0.92rem', textAlign: 'center' }}>{displayType}</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #dddddd', py: 0.95, color: '#2a2a2a', fontSize: '0.92rem' }}>
                                    {file.lastSuccessUpdate
                                        ? dayjs(file.lastSuccessUpdate).format('YYYY/MM/DD - HH:mm:ss')
                                        : ''}
                                </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #dddddd', py: 0.95, fontSize: '0.92rem' }}>
                                    {displayType === 'url' && file.refreshRate ? (
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => {
                                                setRefreshDialogFile(file);
                                                setRefreshDialogValue(file.refreshRate || 'Never');
                                                setRefreshDialogOpen(true);
                                            }}
                                            sx={{ textTransform: 'none', color: '#1976d2', fontSize: '0.92rem' }}
                                        >
                                            {t(`files.${file.refreshRate}`)}
                                        </Button>
                                    ) : ''}
                                </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #dddddd', py: 0.95, color: statusNorm === 'FAILED' ? 'error.main' : '#2a2a2a', fontSize: '0.92rem', fontWeight: statusNorm === 'FAILED' ? 700 : 400 }}>
                                    {localizedStatus}
                                </TableCell>
                            </TableRow>
                        );})}
                    </TableBody>
                </Table>
                )}
            </TableContainer>

            {/* Refresh rate dialog */}
            <Dialog
                open={refreshDialogOpen}
                onClose={() => setRefreshDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>{t('files.modifyRefresh')}</DialogTitle>
                <DialogContent>
                    {refreshDialogFile && (
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 1,
                                wordBreak: 'break-all',
                                color: 'text.secondary',
                            }}
                        >
                            {refreshDialogFile.url || refreshDialogFile.documentName}
                        </Typography>
                    )}
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label={t('files.refresh', 'Refresh')}
                        value={refreshDialogValue}
                        onChange={(e) => setRefreshDialogValue(e.target.value)}
                        sx={{
                            mt: 1,
                            '& .MuiSelect-select': {
                                textTransform: 'none',
                            },
                        }}
                    >
                        <MenuItem value="never">{t('files.never')}</MenuItem>
                        <MenuItem value="daily">{t('files.daily')}</MenuItem>
                        <MenuItem value="weekly">{t('files.weekly')}</MenuItem>
                        <MenuItem value="monthly">{t('files.monthly')}</MenuItem>
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRefreshDialogOpen(false)}>
                        {t('files.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={updatingRefresh}
                        onClick={async () => {
                            if (!refreshDialogFile) return;
                            try {
                                setUpdatingRefresh(true);
                                await fileService.updateUrlRefresh(
                                    selectedBase,
                                    refreshDialogFile.documentID,
                                    refreshDialogValue,
                                    refreshDialogFile.url || refreshDialogFile.documentName,
                                );
                                setSnackbarSeverity('success');
                                setSnackbarMessage(t('files.refreshUpdated', 'Refresh updated'));
                                setOpenSnackbar(true);
                                setRefreshDialogOpen(false);
                                setRefreshDialogFile(null);
                                await fetchFiles();
                                setTimeout(() => {
                                    fetchFiles();
                                }, 4000);
                            } catch (e) {
                                console.error('Error updating refresh rate', e);
                                setSnackbarSeverity('error');
                                setSnackbarMessage(
                                    e?.message || t('files.refreshUpdateError', 'Failed to update refresh'),
                                );
                                setOpenSnackbar(true);
                            } finally {
                                setUpdatingRefresh(false);
                            }
                        }}
                    >
                        {updatingRefresh ? t('files.saving', 'Saving...') : t('files.save', 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Loading Popup */}
            <Dialog
                open={loadingFiles}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        padding: 2,
                        minWidth: 320,
                        textAlign: 'center',
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {t('files.loadingTitle', 'Loading Files')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('files.loadingMessage', 'Loading files, please wait...')}
                    </DialogContentText>
                </DialogContent>
            </Dialog>

            {/* Delete Button */}
            <Button
                variant="contained"
                color="error"
                onClick={handleDeleteSelected}
                disabled={selectedFiles.length === 0}
                sx={{ mt: 2, textTransform: 'none' }}
            >
                {t('files.deleteSelectedFiles')}
            </Button>

            {/* Snackbar Notification */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            <Dialog
                open={openConfirmDialog}
                onClose={() => setOpenConfirmDialog(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        padding: 2,
                        width: '300px', // Adjust size if needed
                    },
                }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    {t('files.confirmDeleteTitle')}
                </DialogTitle>
                <DialogContent>

                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2 }}>
                    <Button
                        onClick={() => setOpenConfirmDialog(false)}
                        sx={{
                            textTransform: 'none',
                            color: 'primary.main',
                            fontWeight: 'bold',
                        }}
                    >
                        {t('files.cancel')}
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        color="error"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            padding: '6px 16px',
                            '&:hover': { backgroundColor: 'error.dark' },
                        }}
                        autoFocus
                    >
                        {t('files.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>


        </Box>
    );
}

export default Files;
